import {
	type AppState,
	type Auth0ContextInterface,
	type User as Auth0User,
	type LogoutOptions,
	type RedirectLoginOptions,
} from "@auth0/auth0-react";
import {
	type ActorRefFromLogic,
	type SnapshotFrom,
	assign,
	createActor,
	fromPromise,
	sendTo,
	setup,
	waitFor,
} from "xstate";

import { setUserProperty } from "@moment/logging";

import { type GetAccessTokenSilently } from "~/auth/AuthProvider";
import { isDesktopAppMode } from "~/auth/interop/common";
import { getLoginAction } from "~/auth/loginAction";
import { getLogoutAction } from "~/auth/logoutAction";
import { clearRedirectStore } from "~/auth/redirectStore";
import { type User } from "~/models/user";
import { analytics } from "~/utils/analytics";

import { coerceToError } from "../coerceToError";
import { mapUser } from "./mapUser";

export interface LoginControls {
	loginWithRedirect: (opts: { redirectPath?: string }) => Promise<void>;
	logoutWithRedirect: (redirectPath?: string) => Promise<void>;

	/**
	 * Request an up-to-date, valid access token. This is called "silently"
	 * as in the access token request process itself is completely invisible
	 * to the user (there are visible ways to perform it as well).
	 *
	 * For more information on how this function works, view the documentation
	 * for the auth0-react library.
	 *
	 * @param {boolean} [opts.promptForLogin=false] - use true in order to automatically
	 * launch a login prompt if the user's authentication appears to be out of date.
	 * The mechanics that trigger this can be opaque, and confusing.
	 * A user may still be successfully authenticated unable to receive
	 * a new valid authentication token if their refresh token is invalid.
	 * This flag is therefore kind of aggressive and fundamentally disruptive
	 * to the user, so use it carefully.
	 * @param {string} opts.redirectPath - if promptForLogin is true, also pass
	 * this value to send the user to a specific page after successful login. Typically
	 * this means using `router.asPath` in order to redirect the user back to the current
	 * page. If this is unset, the user will be sent to `/docs` after login.
	 */
	getAccessTokenSilently: (opts?: {
		promptForLogin?: boolean;
		redirectPath?: string;
	}) => Promise<string | undefined>;
}

export type LoginContext = LoginControls &
	(
		| { authnStatus: "unknown" }
		| { authnStatus: "unauthenticated" }
		| {
				authnStatus: "authenticated";

				/**
				 * The Moment definition of a User. If for some reason you need the Auth0
				 * view of a user, it can be found as _Auth0User.
				 */
				user: User;

				_Auth0User?: Auth0User;
		  }
	);

export type LoginEvent =
	| {
			type: "context.receivedAuth0StateUpdate";
			isAuthenticated: boolean;
			user: Auth0User | undefined;
			loginWithRedirect: (options?: RedirectLoginOptions<AppState>) => Promise<void>;
			logout: (options?: LogoutOptions) => Promise<void>;
			getAccessTokenSilently: Auth0ContextInterface["getAccessTokenSilently"];
	  }
	| { type: "authn.succeeded"; user: Auth0User }
	| { type: "authn.failed"; error: Error }
	| { type: "authn.logout" }
	| ({ type: "authn.token.requested" } & Parameters<GetAccessTokenSilently>[0])
	| { type: "authn.token.received"; token: string }
	| { type: "authn.token.error"; error: Error };

const INVALID_STATE_TRANSITION_ERROR = new Error("Invalid state transition");
const NOT_AUTHENTICATED_ERROR = new Error("Not authenticated");

export type AuthenticationMachine = typeof authenticationMachine;

export const authenticationMachine = setup({
	types: {
		context: {} as LoginContext,
		events: {} as LoginEvent,
	},
	actors: {
		requestToken: fromPromise(
			({
				input: { getAccessTokenSilently, opts },
			}: {
				input: {
					getAccessTokenSilently: GetAccessTokenSilently;
					opts: Parameters<GetAccessTokenSilently>[0];
				};
			}) => getAccessTokenSilently(opts)
		),
	},
	guards: {
		authnStatusAuthenticated: ({ context }) => context.authnStatus === "authenticated",
		authnStatusUnauthenticated: ({ context }) => context.authnStatus === "unauthenticated",
		authnStatusUnknown: ({ context }) => context.authnStatus === "unknown",
	},
	actions: {
		setContextFromAuth0State: assign(({ context, event }) => {
			if (event?.type !== "context.receivedAuth0StateUpdate") {
				return context;
			}

			clearRedirectStore();
			const user = mapUser(event.user);

			if (event.isAuthenticated && user) {
				setUserProperty("email", user.email);
				setUserProperty("isMoment", user.isMomentEmployee);
				setUserProperty("id", user.id);
				setUserProperty("name", user.name);
				analytics.identify(user.id, user);
			}

			const loginAction = (opts: { redirectPath?: string }) => {
				const loginAction = getLoginAction(event.loginWithRedirect, {
					isLocalApp: isDesktopAppMode(),
				});

				sendTo("app", {
					type: "workspaceList.refetch",
				});

				sendTo("app", {
					type: "documentList.refetch",
				});

				return loginAction(opts.redirectPath ?? "/docs");
			};

			const logoutAction = (redirectPath?: string) => {
				const logoutAction = getLogoutAction(event.logout, {
					isLocalApp: isDesktopAppMode(),
				});

				sendTo("app", {
					type: "workspaceList.refetch",
				});

				sendTo("app", {
					type: "documentList.refetch",
				});

				analytics.reset();

				return logoutAction(redirectPath ?? "/docs");
			};

			const getAccessTokenSilently = (
				opts: { promptForLogin?: boolean; redirectPath?: string } = {}
			) => {
				return event.getAccessTokenSilently().catch((e) => {
					if (e.error === `invalid_grant`) {
						if (opts.promptForLogin) {
							console.log(`Login was invalid, redirecting to Login page`);
							void loginAction({ redirectPath: opts.redirectPath });
						} else {
							void logoutAction(opts.redirectPath);
						}
					}
					return Promise.reject(e);
				});
			};

			if (!event.isAuthenticated || !user) {
				return {
					authnStatus: "unauthenticated",
					loginWithRedirect: loginAction,
					logoutWithRedirect: logoutAction,
					getAccessTokenSilently,
				} satisfies LoginContext;
			}

			return {
				authnStatus: "authenticated",
				user,
				loginWithRedirect: loginAction,
				logoutWithRedirect: logoutAction,
				getAccessTokenSilently,
			} satisfies LoginContext;
		}),
	},
}).createMachine({
	id: "authentication",
	initial: "unknown",
	context: {
		authnStatus: "unknown",
		loginWithRedirect: async () => void console.error("Login called before auth initialized"),
		logoutWithRedirect: async () => void console.error("Logout called before auth initialized"),
		getAccessTokenSilently: async () =>
			void console.error("Get access token called before auth initialized"),
	},
	on: { "context.receivedAuth0StateUpdate": { actions: ["setContextFromAuth0State"] } },
	states: {
		unknown: {
			always: [
				{ target: "authenticated", guard: "authnStatusAuthenticated" },
				{ target: "unauthenticated", guard: "authnStatusUnauthenticated" },
			],
		},
		authenticated: {
			initial: "idle",
			always: [
				{ target: "#authentication.unknown", guard: "authnStatusUnknown" },
				{
					target: "#authentication.unauthenticated",
					guard: "authnStatusUnauthenticated",
				},
			],
			states: {
				idle: { on: { "authn.token.requested": "requestingToken" } },
				requestingToken: {
					invoke: {
						src: "requestToken",
						input: ({ context, event }) => {
							if (event.type !== "authn.token.requested") {
								throw INVALID_STATE_TRANSITION_ERROR;
							}
							if (context.authnStatus !== "authenticated") {
								throw NOT_AUTHENTICATED_ERROR;
							}

							return {
								getAccessTokenSilently: context.getAccessTokenSilently,
								opts: event,
							};
						},
						onDone: { target: "idle" },
						onError: [
							{
								target: "#authentication.unauthenticated",
								guard: "authnStatusUnauthenticated",
							},
							{
								target: "#authentication.unknown",
								guard: "authnStatusUnknown",
							},
							{
								target: "#authentication.unauthenticated",
								guard: ({ event }) => event.error === NOT_AUTHENTICATED_ERROR,
							},
							{
								// Bad state transition, let's go back to the unknown state and
								// recover from first principles.
								target: "#authentication.unknown",
								guard: ({ event }) =>
									event.error === INVALID_STATE_TRANSITION_ERROR,
							},
							{
								target: "idle",
								guard: "authnStatusAuthenticated",
								actions: ({ event }) => console.error(coerceToError(event.error)),
							},
							{ target: "#authentication.unknown" },
						],
					},
				},
			},
		},
		unauthenticated: {
			always: [
				{ target: "authenticated", guard: "authnStatusAuthenticated" },
				{ target: "unknown", guard: "authnStatusUnknown" },
			],
		},
	},
});

export const authenticationActorRef = createActor(authenticationMachine);
authenticationActorRef.start();

export const authnStateNotUnknown = (snap: SnapshotFrom<typeof authenticationActorRef>) =>
	!snap.matches("unknown");

export const waitForAuthnStateNotUnknown = () =>
	waitFor(authenticationActorRef, authnStateNotUnknown);

export const waitForAccessTokenSilently = async (
	authenticationActor: ActorRefFromLogic<AuthenticationMachine>,
	opts?: { promptForLogin?: boolean; redirectPath?: string }
) => {
	const snap = await waitFor(authenticationActor, authnStateNotUnknown);
	return await snap.context.getAccessTokenSilently(opts);
};
